import React, { useState, useRef } from 'react';
import { Text, ViewStyle, TextStyle, View, Animated } from 'react-native';
import { colorPalette } from '@shared/config/colorsConfig';
import { typography } from '@shared/config/typographyConfig';

import { Pressable } from 'react-native';
import { Icon } from '@ant-design/react-native';
import { IconNames } from '@ant-design/react-native/lib/icon';
export type CustomButtonVariant =
  | 'primary'
  | 'outline'
  | 'secondary'
  | 'secondary-outline'
  | 'tiffany'
  | 'danger'
  | 'danger-outline'
  | 'link';

export interface ICustomButtonProps {
  variant?: CustomButtonVariant;
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  onPress?: () => void;
  children?: React.ReactNode;
  style?: ViewStyle;
  textStyle?: TextStyle;
  icon?: IconNames;
  iconPosition?: 'left' | 'right';
  customIcon?: React.ReactNode;
}


export const Button: React.FC<ICustomButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  disabled = false,
  onPress,
  children,
  style,
  textStyle,
  icon,
  customIcon,
  iconPosition = 'left',
}) => {
  const [isPressed, setIsPressed] = useState(false);
  const scaleAnim = useRef(new Animated.Value(1)).current;

  const getVariantStyles = (): { buttonStyle: ViewStyle; textStyle: TextStyle } => {
    const baseStyles = {
      small: {
        minHeight: 32,
        paddingHorizontal: 12,
        paddingVertical: 6,
        fontSize: typography.fontSize.sm,
        borderRadius: 6,
      },
      medium: {
        minHeight: 40,
        paddingHorizontal: 16,
        paddingVertical: 8,
        fontSize: typography.fontSize.base,
        borderRadius: 8,
      },
      large: {
        minHeight: 48,
        paddingHorizontal: 20,
        paddingVertical: 12,
        fontSize: typography.fontSize.xl,
        borderRadius: 10,
      },
    };

    const sizeStyle = baseStyles[size];

    const baseButtonStyle: ViewStyle = {
      minHeight: sizeStyle.minHeight,
      paddingHorizontal: sizeStyle.paddingHorizontal,
      paddingVertical: sizeStyle.paddingVertical,
      borderRadius: sizeStyle.borderRadius,
      justifyContent: 'center',
      alignItems: 'center',
      borderWidth: 1,
      marginVertical: 8
    };

    const baseTextStyle: TextStyle = {
      fontSize: sizeStyle.fontSize,
      fontWeight: '500',
      textAlign: 'center',
    };

    if (disabled) {
      return {
        buttonStyle: {
          ...baseButtonStyle,
          backgroundColor: colorPalette.gray[200],
          borderColor: colorPalette.gray[300],
          opacity: 0.6,
        },
        textStyle: {
          ...baseTextStyle,
          color: colorPalette.gray[500],
        },
      };
    }

    switch (variant) {
      case 'primary':
        return {
          buttonStyle: {
            ...baseButtonStyle,
            backgroundColor: isPressed ? colorPalette.primary[600] : colorPalette.primary[700],
            borderColor: isPressed ? colorPalette.primary[600] : colorPalette.primary[700],
          },
          textStyle: {
            ...baseTextStyle,
            color: '#fff',
          },
        };

      case 'outline':
        return {
          buttonStyle: {
            ...baseButtonStyle,
            backgroundColor: isPressed ? colorPalette.primary[100] : colorPalette.white,
            borderColor: isPressed ? colorPalette.primary[600] : colorPalette.primary[700],
          },
          textStyle: {
            ...baseTextStyle,
            color: isPressed ? colorPalette.primary[600] : colorPalette.primary[700],
          },
        };
      case 'secondary':
        return {
          buttonStyle: {
            ...baseButtonStyle,
            backgroundColor: isPressed ? colorPalette.secondary[500] : colorPalette.secondary[600],
            borderColor: isPressed ? colorPalette.secondary[500] : colorPalette.secondary[600],
          },
          textStyle: {
            ...baseTextStyle,
            color: isPressed ? colorPalette.black : colorPalette.white,
          },
        };
      case 'secondary-outline':
        return {
          buttonStyle: {
            ...baseButtonStyle,
            backgroundColor: isPressed ? colorPalette.secondary[100] : colorPalette.white,
            borderColor: isPressed ? colorPalette.secondary[500] : colorPalette.secondary[600],

          },
          textStyle: {
            ...baseTextStyle,
            color: isPressed ? colorPalette.secondary[500] : colorPalette.secondary[600],
          },
        };
      case 'tiffany':
        return {
          buttonStyle: {
            ...baseButtonStyle,
            backgroundColor: isPressed ? colorPalette.accent[500] : colorPalette.accent[100],
            borderColor: isPressed ? colorPalette.accent[500] : colorPalette.accent[100],
          },
          textStyle: {
            ...baseTextStyle,
            fontWeight: '500',
            color: colorPalette.black,
          },
        };
      case 'danger':
        return {
          buttonStyle: {
            ...baseButtonStyle,
            backgroundColor: isPressed ? colorPalette.error[700] : colorPalette.error[500],
            borderColor: isPressed ? colorPalette.error[700] : colorPalette.error[500],
          },
          textStyle: {
            ...baseTextStyle,
            color: colorPalette.white
          },
        };


      case 'danger-outline':
        return {
          buttonStyle: {
            ...baseButtonStyle,
            backgroundColor: isPressed ? colorPalette.error[100] : colorPalette.white,
            borderColor: isPressed ? colorPalette.error[700] : colorPalette.error[500],
          },
          textStyle: {
            ...baseTextStyle,
            color: isPressed ? colorPalette.error[700] : colorPalette.error[500],
          },
        }

      case "link":
        return {
          buttonStyle: {
            ...baseButtonStyle,
            backgroundColor: 'transparent',
            borderColor: 'transparent',
            alignSelf: 'flex-start',
            paddingHorizontal: 0,
            paddingVertical: 0,
            minHeight: 0,
            marginVertical: 0,
            marginHorizontal: 0,
          },
          textStyle: {
            ...baseTextStyle,
            textDecorationLine: 'underline',
            textDecorationColor: isPressed ? colorPalette.primary[500] : colorPalette.primary[600],
            color: isPressed ? colorPalette.primary[500] : colorPalette.primary[600]
          },

        };


      default:
        return {
          buttonStyle: baseButtonStyle,
          textStyle: baseTextStyle,
        };
    }
  };

  const { buttonStyle, textStyle: computedTextStyle } = getVariantStyles();

  const handlePressIn = () => {
    if (!disabled) {
      setIsPressed(true);
      Animated.timing(scaleAnim, {
        toValue: 0.98,
        duration: 100,
        useNativeDriver: true,
      }).start();
    }
  };

  const handlePressOut = () => {
    setIsPressed(false);
    Animated.timing(scaleAnim, {
      toValue: 1,
      duration: 100,
      useNativeDriver: true,
    }).start();
  };

  const handlePress = () => {
    if (!disabled && onPress) {
      onPress();
    }
  };

  return (
    <Animated.View style={{ transform: [{ scale: scaleAnim }] }}>
      <Pressable
        style={[buttonStyle, style]}
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
        onPress={handlePress}
        disabled={disabled}
      >
        <View style={{ flexDirection: iconPosition === 'left' ? 'row' : 'row-reverse', gap: 8, alignItems: 'center' }}>
          {customIcon ? customIcon : icon && <Icon name={icon} size={getVariantStyles().textStyle.fontSize} color={getVariantStyles().textStyle.color as string} />}
          {typeof children === 'string' ? <Text numberOfLines={1} style={[computedTextStyle, textStyle]}>{children}</Text> : children}
        </View>
      </Pressable>
    </Animated.View>
  );
};

export default Button;
